import { useCallback, useEffect, useState } from 'react';
import { campaignService } from '../../backend/services/campaigns';

interface CampaignCount {
  total: number;
  active: number;
}

interface CampaignCountsState {
  artistCounts: Record<string, CampaignCount>;
  releaseCounts: Record<string, CampaignCount>;
  loading: boolean;
  error: string | null;
}

const defaultState: CampaignCountsState = {
  artistCounts: {},
  releaseCounts: {},
  loading: true,
  error: null
};

export const useCampaignCounts = () => {
  const [state, setState] = useState<CampaignCountsState>(defaultState);

  const fetchCounts = useCallback(async () => {
    try {
      setState(prev => ({ ...prev, loading: true, error: null }));
      const { perArtist, perRelease } = await campaignService.getCampaignCountsByEntity();
      setState({
        artistCounts: perArtist,
        releaseCounts: perRelease,
        loading: false,
        error: null
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load campaign counts';
      setState(prev => ({ ...prev, loading: false, error: message }));
    }
  }, []);

  useEffect(() => {
    fetchCounts();
  }, [fetchCounts]);

  useEffect(() => {
    const handleRefresh = () => {
      fetchCounts();
    };

    window.addEventListener('campaigns-updated', handleRefresh);
    return () => {
      window.removeEventListener('campaigns-updated', handleRefresh);
    };
  }, [fetchCounts]);

  return {
    artistCounts: state.artistCounts,
    releaseCounts: state.releaseCounts,
    loading: state.loading,
    error: state.error,
    refetch: fetchCounts
  };
};

export type { CampaignCount };
